import { BdsGrid, BdsInput, BdsPaper, BdsTabGroup, BdsTabItem } from 'blip-ds/dist/blip-ds-react/components';
import { ChipTag } from 'components/ChipTag';
import { Pagination } from 'components/Pagination';
import { Skeleton } from 'components/Skeleton/Skeleton';
import { TicketSideBar } from 'components/TicketSideBar';
import { DEFAULT_PAGE_SIZE, EMPTY_TABLE_DATA, PAGE_SIZE_OPTIONS } from 'constants/utils';
import { useAppContext } from 'contexts/AppContext';
import { useLoadingContext } from 'contexts/LoadingContext';
import { useDataBlockRefs } from 'hooks/useDataBlockRefs';
import { useInitialization } from 'hooks/useInitialization';
import { useTrack } from 'hooks/useTrack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiService } from 'services/Api';
import { getPacksInfo } from 'services/Packs/getPacksInfo';
import { AgentResponse, DataBlockProps, TagResponse } from 'typings';
import { TableDataModel } from 'typings/components/TableDataModel';
import { TicketMessage, TicketMessageResponse } from 'typings/components/TicketMessage';
import TicketTabsEnum from 'typings/enums/TicketTabsEnum';
import { filterArray, filterTicketArray, paginate } from 'utils';
import { AgentsTable } from './AgentsTable';
import './DataBlock.css';
import './DataBlockTable.scss';
import { TagsTable } from './TagsTable';
import { TicketsInAttendanceTable } from './TicketsInAttendanceTable';
import { TicketsInQueueTable } from './TicketsInQueueTable';
import { fetchAgents, fetchTags } from './services/tickets';
import './style.scss';

export const DataBlock: React.FC<DataBlockProps> = ({
    selectedBots,
    isBotsLoading,
    ticketsInAttendance,
    ticketsInQueue,
    setTicketsInAttendance,
    setTicketsInQueue,
    handleFetchTicketsInAttendance,
    handleFetchTicketsInQueue,
    isLoadingTicketsInAttendance,
    isLoadingTicketsInQueue,
}: DataBlockProps) => {
    const { t } = useTranslation();
    const { isToUpdateAll, setDataBlockIsLoading, setIsToUpdateAll } = useLoadingContext();
    const {
        appServices: { reportService },
    } = useAppContext();
    const { isInitialized, markAsInitialized } = useInitialization();

    const { searchRef, pendingPageSizeRef, clearSearch, getSearchTerm, createNewAbortController, setDebounceTimeout } =
        useDataBlockRefs();

    const { track } = useTrack();
    const [isLoadingAgents, setIsLoadingAgents] = useState(false);
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    const [page, setPage] = useState(1);
    const [activeTab, setActiveTab] = useState('ticketsInAttendance');
    const [agents, setAgents] = useState<TableDataModel<AgentResponse['attendants']>>(EMPTY_TABLE_DATA);
    const [tags, setTags] = useState<TableDataModel<TagResponse['tags']>>(EMPTY_TABLE_DATA);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [customerName, setCustomerName] = useState('');
    const [contactMessages, setContactMessages] = useState<TicketMessage[]>();
    const [sideBarLoading, setSideBarLoading] = useState(false);
    const [employeeApi, setEmployeeApi] = useState<ApiService>(new ApiService('', '', '', ''));
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const percentageLoaded = useMemo(() => {
        switch (activeTab) {
            case TicketTabsEnum.TicketsInAttendance:
                return ticketsInAttendance.percentageLoaded;
            case TicketTabsEnum.TicketsInQueue:
                return ticketsInQueue.percentageLoaded;
            case TicketTabsEnum.Agents:
                return agents.percentageLoaded;
            case TicketTabsEnum.Tags:
                return tags.percentageLoaded;
            default:
                return 0;
        }
    }, [
        activeTab,
        agents.percentageLoaded,
        tags.percentageLoaded,
        ticketsInAttendance.percentageLoaded,
        ticketsInQueue.percentageLoaded,
    ]);

    const bots = useMemo(() => {
        return selectedBots.map(b => b.transhipmentSkill);
    }, [selectedBots]);

    const botsKey = useMemo(() => bots.join(','), [bots]);
    const botsRef = useRef(bots);
    botsRef.current = bots;

    const handleFetchTicketsInAttendanceRef = useRef(handleFetchTicketsInAttendance);
    handleFetchTicketsInAttendanceRef.current = handleFetchTicketsInAttendance;
    const handleFetchTicketsInQueueRef = useRef(handleFetchTicketsInQueue);
    handleFetchTicketsInQueueRef.current = handleFetchTicketsInQueue;

    const totalItems = useMemo(() => {
        switch (activeTab) {
            case TicketTabsEnum.TicketsInAttendance:
                return ticketsInAttendance.data.length;
            case TicketTabsEnum.TicketsInQueue:
                return ticketsInQueue.data.length;
            case TicketTabsEnum.Agents:
                return agents.data.length;
            case TicketTabsEnum.Tags:
                return tags.data.length;
            default:
                return 0;
        }
    }, [activeTab, agents.data.length, tags.data.length, ticketsInAttendance.data.length, ticketsInQueue.data.length]);

    const currentTabIsLoading = useMemo(() => {
        switch (activeTab) {
            case TicketTabsEnum.TicketsInAttendance:
                return isLoadingTicketsInAttendance;
            case TicketTabsEnum.TicketsInQueue:
                return isLoadingTicketsInQueue;
            case TicketTabsEnum.Agents:
                return isLoadingAgents;
            case TicketTabsEnum.Tags:
                return isLoadingTags;
            default:
                return false;
        }
    }, [activeTab, isLoadingTicketsInAttendance, isLoadingTicketsInQueue, isLoadingAgents, isLoadingTags]);

    const createEmployeeApi = useCallback(async () => {
        const { accessToken } = await getPacksInfo();
        const employeeApi = new ApiService(import.meta.env.VITE_EMPLOYEE_BASE_URL ?? '', '', accessToken, '');
        setEmployeeApi(employeeApi);
    }, [setEmployeeApi]);

    const openSideBar = useCallback(
        async (ticketNumber: number, deskBotId: string, customerName: string) => {
            setSidebarOpen(true);
            setCustomerName(customerName);
            setContactMessages([]);
            setSideBarLoading(true);
            try {
                const routerShortName = selectedBots.find(
                    botInfo => botInfo.transhipmentSkill === deskBotId,
                )?.instantiatedRouter;
                const response = await employeeApi.get<TicketMessageResponse>(
                    `/v1/Message/Contact/${routerShortName}/${ticketNumber}?MessagesNumber=50`,
                );
                setContactMessages(response.items);
            } catch (error) {
                console.error('Error fetching contact messages:', error);
            }
            setSideBarLoading(false);
        },
        [selectedBots, employeeApi],
    );

    const resetFilters = useCallback(() => {
        clearSearch();
    }, [clearSearch]);

    const resetPercentageLoaded = useCallback(() => {
        setTicketsInAttendance(prev => ({ ...prev, percentageLoaded: 0 }));
        setTicketsInQueue(prev => ({ ...prev, percentageLoaded: 0 }));
        setAgents(prev => ({ ...prev, percentageLoaded: 0 }));
        setTags(prev => ({ ...prev, percentageLoaded: 0 }));
    }, [setTicketsInAttendance, setTicketsInQueue, setAgents, setTags]);

    const filterPaginate = useCallback(
        (filteredData: any[], page: number) => {
            return paginate(filteredData, pageSize, page);
        },
        [pageSize],
    );

    const filterDataSync = useCallback(
        (searchTerm: string, page?: number) => {
            const targetPage = page ?? 1;

            if (page === undefined) {
                setPage(targetPage);
            }

            switch (activeTab) {
                case TicketTabsEnum.TicketsInAttendance:
                    setTicketsInAttendance(prev => ({
                        ...prev,
                        currentPage: targetPage,
                        searchText: searchTerm,
                        filteredData: filterPaginate(filterTicketArray(prev, searchTerm), targetPage),
                    }));
                    break;
                case TicketTabsEnum.TicketsInQueue:
                    setTicketsInQueue(prev => ({
                        ...prev,
                        currentPage: targetPage,
                        searchText: searchTerm,
                        filteredData: filterPaginate(filterTicketArray(prev, searchTerm), targetPage),
                    }));
                    break;
                case TicketTabsEnum.Agents:
                    setAgents(prev => ({
                        ...prev,
                        currentPage: targetPage,
                        searchText: searchTerm,
                        filteredData: filterPaginate(filterArray(prev?.data, searchTerm), targetPage),
                    }));
                    break;
                case TicketTabsEnum.Tags:
                    setTags(prev => ({
                        ...prev,
                        currentPage: targetPage,
                        searchText: searchTerm,
                        filteredData: filterPaginate(filterArray(prev?.data, searchTerm), targetPage),
                    }));
                    break;
                default:
                    break;
            }
        },
        [activeTab, filterPaginate, setTicketsInAttendance, setTicketsInQueue, setAgents, setTags, setPage],
    );

    const filterData = useCallback(
        async (page?: number) => {
            const searchTerm = await getSearchTerm();
            filterDataSync(searchTerm, page);
        },
        [getSearchTerm, filterDataSync],
    );

    const debouncedFilterData = useCallback(() => {
        setDebounceTimeout(() => {
            filterData();
        });
    }, [filterData, setDebounceTimeout]);

    const updatePaginatedData = useCallback(
        (pageNumber: number) => {
            switch (activeTab) {
                case TicketTabsEnum.TicketsInAttendance:
                    setTicketsInAttendance(prev => {
                        if (prev.data.length === 0) return prev;
                        return {
                            ...prev,
                            filteredData: paginate(filterTicketArray(prev, prev.searchText), pageSize, pageNumber),
                            currentPage: pageNumber,
                        };
                    });
                    break;
                case TicketTabsEnum.TicketsInQueue:
                    setTicketsInQueue(prev => {
                        if (prev.data.length === 0) return prev;
                        return {
                            ...prev,
                            filteredData: paginate(filterTicketArray(prev, prev.searchText), pageSize, pageNumber),
                            currentPage: pageNumber,
                        };
                    });
                    break;
                case TicketTabsEnum.Agents:
                    setAgents(prev => {
                        if (prev.data.length === 0) return prev;
                        return {
                            ...prev,
                            filteredData: paginate(filterArray(prev.data, prev.searchText), pageSize, pageNumber),
                            currentPage: pageNumber,
                        };
                    });
                    break;
                case TicketTabsEnum.Tags:
                    setTags(prev => {
                        if (prev.data.length === 0) return prev;
                        return {
                            ...prev,
                            filteredData: paginate(filterArray(prev.data, prev.searchText), pageSize, pageNumber),
                            currentPage: pageNumber,
                        };
                    });
                    break;
            }
        },
        [activeTab, pageSize, setTicketsInAttendance, setTicketsInQueue, setAgents, setTags],
    );

    const handleChangePage = (pageNumber: number) => {
        if (pageNumber !== page) {
            setPage(pageNumber);
            updatePaginatedData(pageNumber);
        }
    };

    const handleChangeTab = async (event: CustomEvent) => {
        const tab = event.detail.id;
        if (TicketTabsEnum.TicketsInAttendance === tab) {
            track('blippacks-employee-monitoring-attendance-table');
        }
        resetFilters();
        setActiveTab(tab);

        // Cancel any ongoing requests and create new AbortController
        const newAbortController = createNewAbortController();

        switch (tab) {
            case TicketTabsEnum.Agents:
                setAgents(prev => ({ ...prev, percentageLoaded: 0 }));
                await fetchAgents(
                    reportService,
                    bots,
                    DEFAULT_PAGE_SIZE,
                    setIsLoadingAgents,
                    setAgents,
                    newAbortController,
                );
                break;
            case TicketTabsEnum.Tags:
                setTags(prev => ({ ...prev, percentageLoaded: 0 }));
                await fetchTags(reportService, bots, DEFAULT_PAGE_SIZE, setIsLoadingTags, setTags, newAbortController);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (currentTabIsLoading) {
            setDataBlockIsLoading(true);
        } else {
            setDataBlockIsLoading(false);
        }
    }, [currentTabIsLoading, setDataBlockIsLoading]);

    useEffect(() => {
        createEmployeeApi();
        markAsInitialized();
    }, [createEmployeeApi, markAsInitialized]);

    useEffect(() => {
        if (isInitialized) {
            if (currentTabIsLoading) {
                pendingPageSizeRef.current = pageSize;
            } else {
                const newPage = 1;
                setPage(newPage);
                updatePaginatedData(newPage);
            }
        }
    }, [activeTab, pageSize, currentTabIsLoading, isInitialized, updatePaginatedData, pendingPageSizeRef]);

    useEffect(() => {
        if (!currentTabIsLoading && pendingPageSizeRef.current !== null && isInitialized) {
            const newPage = 1;
            setPage(newPage);
            updatePaginatedData(newPage);
            pendingPageSizeRef.current = null;
        }
    }, [currentTabIsLoading, isInitialized, updatePaginatedData, pendingPageSizeRef]);

    useEffect(() => {
        if (isToUpdateAll || botsRef.current.length > 0) {
            const currentBots = botsRef.current;
            const newAbortController = createNewAbortController();

            resetPercentageLoaded();
            handleFetchTicketsInAttendanceRef.current();
            handleFetchTicketsInQueueRef.current();
            fetchTags(reportService, currentBots, DEFAULT_PAGE_SIZE, setIsLoadingTags, setTags, newAbortController);
            fetchAgents(
                reportService,
                currentBots,
                DEFAULT_PAGE_SIZE,
                setIsLoadingAgents,
                setAgents,
                newAbortController,
            );
            setIsToUpdateAll(false);
        }
    }, [isToUpdateAll, botsKey, createNewAbortController, resetPercentageLoaded, reportService, setIsToUpdateAll]);

    return (
        <>
            <TicketSideBar
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                customerName={customerName}
                contactMessages={contactMessages}
                sideBarLoading={sideBarLoading}
            />
            {isBotsLoading ? (
                <Skeleton direction="row" skeletons={[{ shape: 'square', width: '100%', height: '300px' }]} />
            ) : (
                <BdsPaper bgColor="surface-0" elevation="none">
                    <BdsGrid direction="column" md="12" padding="2" className="data-block-content">
                        <BdsGrid gap="2" padding="r-1" alignItems="center" className="tab-search">
                            <ChipTag
                                value={t('go-personal-loading-percentage', { percentage: percentageLoaded ?? 0 })}
                                color={percentageLoaded === 100 ? 'outline' : 'default'}
                            />
                            <BdsInput
                                className="pull-front"
                                icon="search"
                                ref={searchRef}
                                onInput={debouncedFilterData}
                            />
                        </BdsGrid>
                        <BdsGrid direction="row" padding="b-2" className="data-block-tabs-row">
                            <BdsGrid md="12" justifyContent="flex-start" className="data-block-tabs">
                                <BdsTabGroup align="left" contentScrollable={false} onBdsTabChange={handleChangeTab}>
                                    <BdsTabItem
                                        label={t('go-personal-data-block-tab-tickets-attendance')}
                                        id={TicketTabsEnum.TicketsInAttendance}
                                        headerStyle="padding: 0; flex-shrink: 0"
                                    >
                                        <TicketsInAttendanceTable
                                            openSideBar={openSideBar}
                                            data={ticketsInAttendance.filteredData}
                                            isLoading={currentTabIsLoading}
                                            botsInfos={selectedBots}
                                            onTicketChanged={() => setIsToUpdateAll(true)}
                                        />
                                    </BdsTabItem>
                                    <BdsTabItem
                                        label={t('go-personal-data-block-tab-tickets-queue')}
                                        id={TicketTabsEnum.TicketsInQueue}
                                        headerStyle="padding: 0; flex-shrink: 0"
                                    >
                                        <TicketsInQueueTable
                                            openSideBar={openSideBar}
                                            data={ticketsInQueue.filteredData}
                                            isLoading={currentTabIsLoading}
                                            botsInfos={selectedBots}
                                        />
                                    </BdsTabItem>
                                    <BdsTabItem
                                        label={t('go-personal-data-block-tab-agents')}
                                        id={TicketTabsEnum.Agents}
                                        headerStyle="padding: 0; flex-shrink: 0"
                                    >
                                        <AgentsTable
                                            data={agents.filteredData}
                                            ticketsInQueue={ticketsInQueue.filteredData}
                                            isLoading={currentTabIsLoading}
                                            hasMultipleBots={selectedBots.length > 1}
                                        />
                                    </BdsTabItem>
                                    <BdsTabItem
                                        label={t('go-personal-data-block-tab-tags')}
                                        id={TicketTabsEnum.Tags}
                                        headerStyle="padding: 0; flex-shrink: 0"
                                    >
                                        <TagsTable data={tags.filteredData} isLoading={currentTabIsLoading} />
                                    </BdsTabItem>
                                </BdsTabGroup>
                            </BdsGrid>
                        </BdsGrid>
                        <BdsGrid md="12" justifyContent="flex-end">
                            <Pagination
                                key={`${activeTab}-${totalItems}`}
                                totalItems={totalItems}
                                itemsPerPage={pageSize}
                                itemsPerPageOptions={PAGE_SIZE_OPTIONS}
                                onItemsPerPageChange={setPageSize}
                                onChange={handleChangePage}
                            />
                        </BdsGrid>
                    </BdsGrid>
                </BdsPaper>
            )}
        </>
    );
};
